/**
 * EditeurFacture — Éditeur de facture style QuickBooks
 * Lignes éditables: N°, Date, Service (dropdown), Description, Qté, Taux, Montant, Taxe
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Save, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/lib/apiUrl';
const fmt = (n) => Number(n || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TAX_OPTIONS = [
  { value: 'tps_tvq', label: 'TPS/TVQ QC - 14,975%', tps: 0.05, tvq: 0.09975 },
  { value: 'tps_only', label: 'TPS seulement - 5%', tps: 0.05, tvq: 0 },
  { value: 'none', label: 'Aucune taxe', tps: 0, tvq: 0 },
];

function emptyLine(num) {
  return {
    // 🆕 [Phase 68] id stable indispensable pour la `key` du <tr> : sans ça,
    // tout changement de `description` modifie la key (via fallback
    // JSON.stringify) et React démonte/remonte le <tr>, faisant perdre le
    // focus du Textarea à chaque keystroke.
    id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    num,
    date: new Date().toISOString().slice(0, 10),
    service_id: '',
    service_nom: '',
    description: '',
    quantite: 1,
    taux: 0,
    montant: 0,
    taxe: 'tps_tvq',
  };
}

export default function EditeurFacture({ isOpen, onClose, dossier, client, infosEntreprise, onSuccess, typeFacture = 'depot', factureDepot = null, ajouts = [], soumissionProp = null, complementaireNumero = null, factureExistante = null }) {
  // 🆕 [Phase 74 — 2026-06-18] Mode édition : factureExistante = la facture à modifier.
  // Quand fourni, le dialog devient un éditeur complet (lignes/totaux/notes) et
  // fait PUT au lieu de POST. Permet de modifier une facture déjà créée sans
  // passer par le mini-dialog limité à 3 champs.
  const isEditMode = !!factureExistante;
  const [soumission, setSoumission] = useState(null);
  const [services, setServices] = useState([]);
  const [lines, setLines] = useState([]);
  const [depotType, setDepotType] = useState('pourcentage');
  const [depotValeur, setDepotValeur] = useState(25);
  const [adresse, setAdresse] = useState('');
  const [ville, setVille] = useState('');
  const [codePostal, setCodePostal] = useState('');
  const [loading, setLoading] = useState(false);
  // 🆕 [2026-06-12 v8] Rabais commercial (montant fixe $) — uniquement pour
  // facture finale ou complémentaire (jamais sur dépôt).
  const [rabaisMontant, setRabaisMontant] = useState(0);
  const [rabaisDescription, setRabaisDescription] = useState('');
  // 🆕 [Phase 75 — 2026-06-18] Notes visibles client (affichées sur la facture +
  // dans le PDF généré). Distinct de notes internes (côté admin uniquement).
  const [notesClient, setNotesClient] = useState('');
  const isCash = dossier?.paiement_en_argent;
  // 🆕 2026-05-15 — type_facture='complementaire' : facture supplémentaire
  // créée APRÈS la finale, pour travaux d'ajout faits post-réception.
  // - Pas de dépôt
  // - Pas de soumission requise (la soumission originale a déjà été finalisée)
  // - Utilise le MÊME template que les autres factures (zéro recréation)
  const isComplementaire = typeFacture === 'complementaire';
  const isFinale = typeFacture === 'finale';

  // 🐛 [Bugfix v13.48.2 — 2026-07-20] Sentinel init lignes.
  // AVANT : les useEffect d'init dépendaient de `soumission`, `client`, `factureExistante`,
  // `isCash` — dès que le parent re-render (ex: user tape dans un autre champ, `soumissionAcceptee`
  // recalculée => nouvelle référence), les hooks re-déclenchaient `setLines(items)` avec `date=today`,
  // ÉCRASANT toute date personnalisée saisie par l'utilisateur.
  // APRÈS : on init les lignes UNE SEULE FOIS par ouverture du dialog. À la fermeture,
  // le ref est reset pour que la prochaine ouverture réinitialise proprement.
  const linesInitialisedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      linesInitialisedRef.current = false;
    }
  }, [isOpen]);

  // Charger les services + utiliser la soumission passée en prop
  useEffect(() => {
    if (!isOpen) return;
    // Charger les services du catalogue via axios
    axios.get(`${API_URL}/api/services?actif_only=true`)
      .then(({ data }) => setServices(data.services || data || []))
      .catch(() => {});

    // Utiliser la soumission passée en prop
    const s = soumissionProp || dossier?.soumissions?.find(s => s.statut === 'acceptee');
    setSoumission(s || null);

    // 🆕 Complémentaire : adresse de facturation pré-remplie depuis le client
    // (la soumission n'est pas requise → on initialise les champs ici).
    if (isComplementaire) {
      setAdresse(client?.adresse_client || '');
      setVille(client?.ville_client || '');
      setCodePostal(client?.code_postal_client || '');
      // 🐛 [Bugfix v13.48.2] N'init les lignes qu'une seule fois par ouverture
      if (!linesInitialisedRef.current) {
        setLines([{ ...emptyLine(1), taxe: isCash ? 'none' : 'tps_tvq' }]);
        linesInitialisedRef.current = true;
      }
    }
  }, [isOpen, soumissionProp, dossier, isComplementaire, client, isCash]);

  // Init depuis soumission
  useEffect(() => {
    if (!isOpen || !soumission) return;
    // 🆕 [Phase 74] En mode édition, on prend l'adresse et le dépôt depuis la
    // facture existante (pas depuis la soumission qui pourrait avoir des
    // valeurs différentes).
    if (isEditMode) return;
    setAdresse(client?.adresse_client || '');
    setVille(client?.ville_client || '');
    setCodePostal(client?.code_postal_client || '');
    setDepotType(soumission.depot_type || 'pourcentage');
    setDepotValeur(soumission.depot_valeur ?? 25);

    const defaultTaxe = isCash ? 'none' : 'tps_tvq';
    const items = (soumission.items || []).map((item, i) => ({
      // 🆕 [Phase 68] id stable obligatoire pour la key du <tr>
      id: item.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `seed-${i}-${Date.now()}`),
      num: i + 1,
      date: new Date().toISOString().slice(0, 10),
      service_id: '',
      service_nom: '',
      description: item.description || '',
      quantite: 1,
      taux: item.montant || 0,
      montant: item.montant || 0,
      taxe: defaultTaxe,
    }));
    if (items.length === 0) items.push(emptyLine(1));
    // 🐛 [Bugfix v13.48.2] N'init les lignes qu'une seule fois par ouverture
    if (!linesInitialisedRef.current) {
      setLines(items);
      linesInitialisedRef.current = true;
    }
  }, [isOpen, soumission, client, isCash, isEditMode]);

  // 🆕 [Phase 74 — 2026-06-18] Init depuis facture existante (mode édition)
  useEffect(() => {
    if (!isOpen || !isEditMode || !factureExistante) return;
    setAdresse(factureExistante.adresse_facturation || '');
    setVille(factureExistante.ville_facturation || '');
    setCodePostal(factureExistante.code_postal_facturation || '');
    setDepotType(factureExistante.depot_type || 'pourcentage');
    setDepotValeur(factureExistante.depot_valeur ?? 25);
    setRabaisMontant(factureExistante.rabais_montant || 0);
    setRabaisDescription(factureExistante.rabais_description || '');
    setNotesClient(factureExistante.notes_client || '');

    const items = (factureExistante.items || []).map((item, i) => ({
      id: item.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `edit-${i}-${Date.now()}`),
      num: item.num ?? i + 1,
      // 🐛 [Bugfix v13.48.6 — 2026-07-20] Le backend persiste `date_service` (nom
      //   historique côté PDF/serveur), mais l'ancienne version du composant relisait
      //   `item.date` — undefined en édition — d'où le fallback silencieux vers today.
      //   On accepte désormais les deux clés, en donnant priorité au format canonique
      //   `date` s'il est présent (nouvelles écritures alignées).
      date: item.date || item.date_service || new Date().toISOString().slice(0, 10),
      service_id: item.service_id || '',
      service_nom: item.service_nom || '',
      description: item.description || '',
      quantite: item.quantite ?? 1,
      taux: item.taux ?? item.montant ?? 0,
      montant: item.montant ?? 0,
      taxe: item.taxe || (isCash ? 'none' : 'tps_tvq'),
    }));
    if (items.length === 0) items.push(emptyLine(1));
    // 🐛 [Bugfix v13.48.2] N'init les lignes qu'une seule fois par ouverture (mode édition)
    if (!linesInitialisedRef.current) {
      setLines(items);
      linesInitialisedRef.current = true;
    }
  }, [isOpen, isEditMode, factureExistante, isCash]);

  const updateLine = (idx, field, value) => {
    setLines(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'quantite' || field === 'taux') {
        const q = field === 'quantite' ? value : next[idx].quantite;
        const t = field === 'taux' ? value : next[idx].taux;
        next[idx].montant = (parseFloat(q) || 0) * (parseFloat(t) || 0);
      }
      return next;
    });
  };

  const selectService = (idx, serviceId) => {
    const svc = services.find(s => s.id === serviceId);
    if (svc) {
      setLines(prev => {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          service_id: svc.id,
          service_nom: svc.nom,
          description: next[idx].description || svc.description || '',
          taux: svc.taux_horaire || next[idx].taux,
          montant: (next[idx].quantite || 1) * (svc.taux_horaire || next[idx].taux),
        };
        return next;
      });
    }
  };

  const addLine = () => {
    const defaultTaxe = isCash ? 'none' : 'tps_tvq';
    setLines(prev => [...prev, { ...emptyLine(prev.length + 1), taxe: defaultTaxe }]);
  };

  const removeLine = (idx) => {
    setLines(prev => prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, num: i + 1 })));
  };

  // Calculs
  const sousTotal = lines.reduce((s, l) => s + (parseFloat(l.montant) || 0), 0);
  // 🆕 [2026-06-12 v8] Appliquer le rabais sur le sous-total AVANT taxes
  // (convention QC : rabais commercial réduit la base taxable).
  // Pour respecter les statuts taxables ligne par ligne, on applique un facteur
  // proportionnel : si rabais = 10 % du sous-total brut, chaque ligne contribue
  // 10 % de moins à tps/tvq. Items exempts de taxe restent exempts.
  const rabaisAppliquable = Math.max(0, Math.min(parseFloat(rabaisMontant) || 0, sousTotal));
  const facteurApresRabais = sousTotal > 0 ? (sousTotal - rabaisAppliquable) / sousTotal : 1;
  const sousTotalApresRabais = sousTotal - rabaisAppliquable;
  const tpsTotal = lines.reduce((s, l) => {
    const tax = TAX_OPTIONS.find(t => t.value === l.taxe) || TAX_OPTIONS[0];
    return s + (parseFloat(l.montant) || 0) * facteurApresRabais * tax.tps;
  }, 0);
  const tvqTotal = lines.reduce((s, l) => {
    const tax = TAX_OPTIONS.find(t => t.value === l.taxe) || TAX_OPTIONS[0];
    return s + (parseFloat(l.montant) || 0) * facteurApresRabais * tax.tvq;
  }, 0);
  const totalAvecTaxes = sousTotalApresRabais + tpsTotal + tvqTotal;
  const montantDepot = depotType === 'pourcentage' ? totalAvecTaxes * (depotValeur / 100) : depotValeur;

  const totalAjouts = ajouts.reduce((s, a) => s + (a.montant_total || 0), 0);
  const montantDepotPaye = factureDepot?.montant_paye_reel || factureDepot?.montant_paye || 0;

  const handleSave = async () => {
    const validLines = lines.filter(l => l.description || l.montant > 0);
    if (validLines.length === 0) { toast.error('Ajoutez au moins une ligne'); return; }
    setLoading(true);
    try {
      const items = validLines.map(l => ({
        description: l.description,
        montant: parseFloat(l.montant) || 0,
        quantite: parseFloat(l.quantite) || 1,
        taux: parseFloat(l.taux) || 0,
        service_id: l.service_id || '',
        service_nom: l.service_nom || '',
        // 🐛 [Bugfix v13.48.6 — 2026-07-20] Écrire les DEUX clés pour rétro-compat.
        //   `date_service` est le nom historique consommé par le PDF/serveur.
        //   `date` est le nom court utilisé par l'UI React — on l'écrit aussi
        //   pour que la ré-ouverture en mode édition récupère la vraie valeur
        //   sans passer par le fallback `today`.
        date: l.date,
        date_service: l.date,
        taxe: l.taxe,
        type: 'service',
      }));

      const payload = {
        dossier_id: dossier.id,
        client_id: dossier.client_id,
        soumission_id: soumission?.id || '',
        type_facture: typeFacture,
        adresse_facturation: adresse,
        ville_facturation: ville,
        code_postal_facturation: codePostal,
        items,
        sous_total: sousTotal,
        tps: tpsTotal,
        tvq: tvqTotal,
      };

      if (isFinale || isComplementaire) {
        // 🆕 Complémentaire : même payload que finale (pas de dépôt), avec un
        // champ supplémentaire `numero_complementaire` pour la numérotation
        // séquentielle par dossier (Comp-001, 002…).
        payload.montant_total = totalAvecTaxes;
        payload.montant_avant_taxes = sousTotalApresRabais;
        payload.paiement_en_argent = isCash;
        // 🆕 [2026-06-12 v8] Persistance du rabais (montant + description)
        payload.rabais_montant = rabaisAppliquable;
        payload.rabais_description = (rabaisDescription || '').trim();
        if (isFinale) {
          payload.montant_ajouts = totalAjouts;
          payload.montant_depot_paye = montantDepotPaye;
        }
        if (isComplementaire) {
          payload.montant_ajouts = totalAjouts; // ajouts complémentaires liés
          payload.montant_depot_paye = 0; // pas de dépôt sur complémentaire
          if (complementaireNumero) {
            payload.numero_complementaire = complementaireNumero;
          }
        }
      } else {
        payload.depot_type = depotType;
        payload.depot_valeur = depotValeur;
        payload.pourcentage_depot = depotType === 'pourcentage' ? depotValeur : (totalAvecTaxes > 0 ? Math.round(montantDepot / totalAvecTaxes * 100) : 0);
        payload.montant_total = montantDepot;
      }

      // 🆕 [Phase 75] Notes visibles client (toujours dans le payload, vide si pas saisi)
      payload.notes_client = (notesClient || '').trim();

      // 🆕 [Phase 74] PUT si mode édition, sinon POST (création)
      if (isEditMode) {
        await axios.put(`${API_URL}/api/crm/factures/${factureExistante.id}`, payload);
        toast.success('Facture modifiée avec succès');
      } else {
        await axios.post(`${API_URL}/api/crm/factures`, payload);
        const successLabel = isFinale ? 'Facture finale créée !' : (isComplementaire ? 'Facture complémentaire créée !' : 'Facture de dépôt créée !');
        toast.success(successLabel);
      }
      onSuccess();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur lors de la creation';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Complémentaire : pas de soumission requise — on affiche directement l'éditeur.
  // 🆕 [Phase 74 — 2026-06-18] Mode édition : bypass aussi la vérif soumission
  //   (la facture existe déjà, elle a sa propre source de vérité via factureExistante).
  if (!isComplementaire && !isEditMode && !soumission) return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Editeur de Facture</DialogTitle></DialogHeader>
        <p className="text-center py-8 text-gray-500">Aucune soumission acceptee trouvee pour ce dossier. Acceptez d'abord une soumission.</p>
        <div className="flex justify-end"><Button variant="outline" onClick={onClose}>Fermer</Button></div>
      </DialogContent>
    </Dialog>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="editeur-facture">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>
              {isEditMode
                ? `Modifier facture ${factureExistante?.numero_facture || ''} — ${dossier?.numero_dossier || ''}`
                : `Editeur de ${isFinale ? 'Facture Finale' : (isComplementaire ? `Facture Complémentaire${complementaireNumero ? ` #${complementaireNumero}` : ''}` : 'Facture de Depot')} — ${dossier?.numero_dossier || ''}`}
            </span>
            {isCash && <Badge className="bg-amber-100 text-amber-800">Paiement argent — Sans taxes</Badge>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Adresse de facturation */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold text-gray-500">Adresse de facturation</Label>
              <Input value={adresse} onChange={e => setAdresse(e.target.value)} placeholder="Adresse" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-500">Ville</Label>
              <Input value={ville} onChange={e => setVille(e.target.value)} placeholder="Ville" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-500">Code postal</Label>
              <Input value={codePostal} onChange={e => setCodePostal(e.target.value)} placeholder="Code postal" className="mt-1" />
            </div>
          </div>

          {/* Tableau des lignes — style QuickBooks */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-sm">Produit ou service</h3>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="facture-lines-table">
                <thead>
                  <tr className="bg-gray-50 border-b text-xs font-semibold text-gray-600">
                    <th className="p-2 w-10 text-center">N</th>
                    <th className="p-2 w-28">Date</th>
                    <th className="p-2 w-40">Produit/service</th>
                    <th className="p-2">Description</th>
                    <th className="p-2 w-16 text-right">Qte</th>
                    <th className="p-2 w-20 text-right">Taux</th>
                    <th className="p-2 w-24 text-right">Montant</th>
                    <th className="p-2 w-44">Taxe de vente</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => (
                    <tr key={line.id || `line-pos-${idx}`} className="border-b hover:bg-gray-50/50" data-testid={`facture-line-${idx}`}>
                      <td className="p-2 text-center text-gray-400">
                        <div className="flex items-center gap-1">
                          <GripVertical className="w-3 h-3 text-gray-300" />
                          {line.num}
                        </div>
                      </td>
                      <td className="p-1">
                        <Input
                          type="date"
                          value={line.date}
                          onChange={e => updateLine(idx, 'date', e.target.value)}
                          className="h-8 text-xs"
                        />
                      </td>
                      <td className="p-1">
                        <Select value={line.service_id || '_manual'} onValueChange={v => v === '_manual' ? updateLine(idx, 'service_id', '') : selectService(idx, v)}>
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="— Choisir —" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_manual">Saisie manuelle</SelectItem>
                            {services.map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-1">
                        <Textarea
                          value={line.description}
                          onChange={e => updateLine(idx, 'description', e.target.value)}
                          className="min-h-[32px] h-8 text-xs resize-none"
                          rows={1}
                        />
                      </td>
                      <td className="p-1">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.quantite}
                          onChange={e => updateLine(idx, 'quantite', parseFloat(e.target.value) || 0)}
                          className="h-8 text-xs text-right"
                        />
                      </td>
                      <td className="p-1">
                        <Input
                          type="number"
                          step="0.01"
                          value={line.taux}
                          onChange={e => updateLine(idx, 'taux', parseFloat(e.target.value) || 0)}
                          className="h-8 text-xs text-right"
                        />
                      </td>
                      <td className="p-2 text-right font-medium text-sm">
                        {fmt(line.montant)} $
                      </td>
                      <td className="p-1">
                        <Select value={line.taxe} onValueChange={v => updateLine(idx, 'taxe', v)}>
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TAX_OPTIONS.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-1 text-center">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => removeLine(idx)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-2 border-t bg-gray-50">
                <Button variant="ghost" size="sm" onClick={addLine} className="text-blue-600 hover:text-blue-800" data-testid="btn-add-line">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter une ligne
                </Button>
              </div>
            </div>
          </div>

          {/* Totaux */}
          <div className="flex justify-end">
            <div className="w-80 space-y-1 text-sm">
              <div className="flex justify-between py-1">
                <span className="text-gray-600">Sous-total</span>
                <span className="font-medium">{fmt(sousTotal)} $</span>
              </div>
              {/* 🆕 [v8] Rabais commercial — visible uniquement sur finale/complémentaire */}
              {(isFinale || isComplementaire) && (
                <div className="border-t border-b py-2 my-1 bg-rose-50/40">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <Label className="text-xs font-semibold text-rose-700" htmlFor="facture-rabais-montant">
                      Rabais commercial ($)
                    </Label>
                    <Input
                      id="facture-rabais-montant"
                      data-testid="facture-rabais-montant-input"
                      type="number"
                      step="0.01"
                      min="0"
                      max={sousTotal}
                      value={rabaisMontant}
                      onChange={e => setRabaisMontant(parseFloat(e.target.value) || 0)}
                      className="h-7 w-28 text-xs text-right"
                      placeholder="0,00"
                    />
                  </div>
                  {rabaisAppliquable > 0 && (
                    <>
                      <Input
                        data-testid="facture-rabais-description-input"
                        type="text"
                        value={rabaisDescription}
                        onChange={e => setRabaisDescription(e.target.value)}
                        placeholder="Raison (ex. : Geste commercial — client fidèle)"
                        className="h-7 text-xs mt-1"
                      />
                      <div className="flex justify-between text-xs text-rose-700 font-medium mt-1">
                        <span>− Rabais appliqué</span>
                        <span data-testid="facture-rabais-applique-display">− {fmt(rabaisAppliquable)} $</span>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="flex justify-between py-1">
                <span className="text-gray-600">TPS (5%)</span>
                <span>{fmt(tpsTotal)} $</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-600">TVQ (9,975%)</span>
                <span>{fmt(tvqTotal)} $</span>
              </div>
              <div className="flex justify-between py-2 border-t font-bold text-base">
                <span>{isFinale ? 'Total travaux' : (isComplementaire ? 'Total complémentaire' : 'Total')}</span>
                <span>{fmt(totalAvecTaxes)} $</span>
              </div>
              {!isFinale && !isComplementaire && (
                <div className="flex items-center gap-2 pt-2 border-t">
                  <span className="text-blue-600 font-medium text-xs">Depot</span>
                  <select className="border rounded px-2 py-1 text-xs" value={depotType} onChange={e => setDepotType(e.target.value)}>
                    <option value="pourcentage">%</option>
                    <option value="dollar">$</option>
                  </select>
                  <Input
                    type="number"
                    step="0.01"
                    value={depotValeur}
                    onChange={e => setDepotValeur(parseFloat(e.target.value) || 0)}
                    className="h-7 w-20 text-xs text-right"
                  />
                  <span className="ml-auto font-bold text-blue-700">{fmt(montantDepot)} $</span>
                </div>
              )}
              {isFinale && (
                <>
                  {totalAjouts > 0 && (
                    <div className="flex justify-between py-1 text-amber-700">
                      <span>+ Ajouts au contrat</span>
                      <span className="font-semibold">{fmt(totalAjouts)} $</span>
                    </div>
                  )}
                  {montantDepotPaye > 0 && (
                    <div className="flex justify-between py-1 text-blue-700">
                      <span>- Depot recu</span>
                      <span className="font-semibold">- {fmt(montantDepotPaye)} $</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-t font-bold text-base text-green-700">
                    <span>Solde du</span>
                    <span>{fmt(Math.max(0, totalAvecTaxes + totalAjouts - montantDepotPaye))} $</span>
                  </div>
                </>
              )}
              {isComplementaire && totalAjouts > 0 && (
                <div className="flex justify-between py-1 text-amber-700">
                  <span>+ Ajouts complémentaires</span>
                  <span className="font-semibold">{fmt(totalAjouts)} $</span>
                </div>
              )}
              {isComplementaire && (
                <div className="flex justify-between py-2 border-t font-bold text-base text-green-700">
                  <span>Total à facturer</span>
                  <span>{fmt(totalAvecTaxes + totalAjouts)} $</span>
                </div>
              )}
            </div>
          </div>

          {/* 🆕 [Phase 75 — 2026-06-21] Notes visibles client — encadré dédié bas.
              S'affichera sur la facture publique + PDF, distinct des notes internes.
              Pratique pour : remerciements, modalités spéciales, références dossier, etc. */}
          <div className="border-2 border-emerald-200 bg-emerald-50/40 rounded-lg p-4 mt-2" data-testid="notes-client-section">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-bold text-emerald-900 flex items-center gap-2">
                Notes visibles client
                <Badge className="bg-emerald-600 text-white text-[10px] font-normal">Visible par le client</Badge>
              </Label>
              {notesClient && (
                <span className="text-[11px] text-emerald-700">
                  {notesClient.length} caractère{notesClient.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <Textarea
              value={notesClient}
              onChange={(e) => setNotesClient(e.target.value)}
              placeholder="Texte qui apparaîtra sur la facture envoyée au client (PDF + page web). Ex: « Merci pour votre confiance », instructions de paiement spéciales, etc."
              rows={3}
              className="bg-white text-sm resize-none"
              data-testid="facture-notes-client-input"
            />
            <p className="text-[11px] text-emerald-700/80 mt-1">
              Ces notes sont distinctes de vos notes internes et seront affichées dans un encadré sur la facture remise au client.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={handleSave} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-save-facture">
              <Save className="w-4 h-4 mr-2" />
              {loading
                ? (isEditMode ? 'Sauvegarde...' : 'Creation...')
                : (isEditMode
                  ? 'Enregistrer les modifications'
                  : (isFinale ? 'Enregistrer la facture finale' : (isComplementaire ? 'Enregistrer la facture complémentaire' : 'Enregistrer la facture de depot')))}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
